import React, { useState, useEffect } from 'react';
import { stepsData } from '../../data/mock';
import {
  ClipboardCheck,
  CreditCard,
  Calendar,
  Phone,
  FileCheck,
  Percent,
  Zap,
  Users,
  LayoutDashboard,
  Send,
  Check
} from 'lucide-react';

const iconMap = {
  ClipboardCheck,
  CreditCard,
  Calendar,
  Phone,
  FileCheck,
  Percent,
  Zap,
  Users,
  LayoutDashboard,
  Send
};

const PathOverview = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Check localStorage for completed steps based on user progress
  useEffect(() => {
    const checkProgress = () => {
      const completed = [];
      
      // Check if user has started pre-approval (saved progress)
      const savedProgress = localStorage.getItem('oneforce_preapproval_progress');
      if (savedProgress) {
        completed.push(1);
      }
      
      // Check if application fee has been paid
      const applicationFeePaid = localStorage.getItem('applicationFeePaid') === 'true';
      if (applicationFeePaid) {
        completed.push(1, 2);
      }
      
      // Check if funding fee has been paid
      const fundingFeePaid = localStorage.getItem('fundingFeePaid') === 'true';
      if (fundingFeePaid) {
        completed.push(1, 2, 3, 4, 5, 6);
      }
      
      setCompletedSteps([...new Set(completed)]);
    };
    
    checkProgress();
    window.addEventListener('storage', checkProgress);
    return () => window.removeEventListener('storage', checkProgress);
  }, []);

  const isStepCompleted = (stepId) => completedSteps.includes(stepId);

  return (
    <section id="path-overview" className="py-20 md:py-32 bg-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-emerald-500/5 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            How the Path to Funding Works
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            You can complete this path at your own pace. Each step exists to create clarity, not pressure.
          </p>
          {completedSteps.length > 0 && (
            <p className="text-green-400 text-sm mt-3">
              <Check className="w-4 h-4 inline mr-1" />
              {completedSteps.length} step{completedSteps.length > 1 ? 's' : ''} completed
            </p>
          )}
        </div>

        {/* Timeline - Desktop */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Connection Line */}
            <div className="absolute top-12 left-0 right-0 h-0.5 bg-slate-700" />
            <div
              className="absolute top-12 left-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
              style={{ width: `${(Math.max(...completedSteps, 0, activeStep - 1) / 9) * 100}%` }}
            />

            {/* Steps */}
            <div className="grid grid-cols-10 gap-4">
              {stepsData.map((step) => {
                const Icon = iconMap[step.icon];
                const isActive = step.id === activeStep;
                const isCompleted = isStepCompleted(step.id);

                return (
                  <div
                    key={step.id}
                    className="flex flex-col items-center cursor-pointer group"
                    onClick={() => setActiveStep(step.id)}
                  >
                    {/* Icon Circle */}
                    <div
                      className={`w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCompleted
                          ? 'bg-green-500 shadow-lg shadow-green-500/30'
                          : isActive
                          ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30 scale-110'
                          : 'bg-slate-800 border-2 border-slate-700 group-hover:border-slate-600'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-10 h-10 text-white" />
                      ) : (
                        <Icon
                          className={`w-8 h-8 transition-colors ${
                            isActive ? 'text-slate-950' : 'text-slate-400 group-hover:text-slate-300'
                          }`}
                        />
                      )}
                    </div>

                    {/* Step Number */}
                    <span
                      className={`mt-4 text-sm font-semibold ${
                        isCompleted ? 'text-green-400' : isActive ? 'text-emerald-400' : 'text-slate-500'
                      }`}
                    >
                      {isCompleted ? '✓ Done' : `Step ${step.id}`}
                    </span>

                    {/* Short Title */}
                    <span
                      className={`mt-1 text-xs text-center ${
                        isCompleted ? 'text-green-400' : isActive ? 'text-white' : 'text-slate-500'
                      }`}
                    >
                      {step.shortTitle}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Timeline - Mobile */}
        <div className="lg:hidden">
          <div className="flex overflow-x-auto pb-4 -mx-4 px-4 space-x-4 scrollbar-hide">
            {stepsData.map((step) => {
              const Icon = iconMap[step.icon];
              const isActive = step.id === activeStep;
              const isCompleted = isStepCompleted(step.id);

              return (
                <button
                  key={step.id}
                  className={`flex-shrink-0 flex flex-col items-center p-4 rounded-xl transition-all ${
                    isCompleted
                      ? 'bg-green-500/20 border-2 border-green-500'
                      : isActive
                      ? 'bg-slate-800 border-2 border-emerald-500'
                      : 'bg-slate-800/50 border-2 border-transparent'
                  }`}
                  onClick={() => setActiveStep(step.id)}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-green-500' : isActive ? 'bg-emerald-500' : 'bg-slate-700'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <Icon
                        className={`w-6 h-6 ${
                          isActive ? 'text-slate-950' : 'text-slate-400'
                        }`}
                      />
                    )}
                  </div>
                  <span
                    className={`mt-2 text-xs font-medium ${
                      isCompleted ? 'text-green-400' : isActive ? 'text-emerald-400' : 'text-slate-500'
                    }`}
                  >
                    {isCompleted ? '✓' : step.id}. {step.shortTitle}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active Step Preview */}
        <div className="mt-12 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <span className={`font-semibold ${isStepCompleted(activeStep) ? 'text-green-400' : 'text-emerald-400'}`}>
                  {isStepCompleted(activeStep) ? '✓ Completed' : `Step ${activeStep}`}
                </span>
                <span className="text-slate-600">•</span>
                <span className="text-slate-400">{stepsData[activeStep - 1].purpose}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                {stepsData[activeStep - 1].title}
              </h3>
              <p className="text-slate-400">
                {stepsData[activeStep - 1].description}
              </p>
            </div>
            <a href="#steps" className="flex-shrink-0">
              <button className="px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/25">
                View Details
              </button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PathOverview;
